import { useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
// Dynamic import to avoid circular dependency with campaign-performance-cache
// import { UnifiedCampaignPerformance } from '@/lib/unified-campaign-performance'
import { useToast } from '@/hooks/use-toast'

interface UseStatsTrackerOptions {
  onStatsUpdate?: () => void
  enableAutoRefresh?: boolean
}

/**
 * Hook to automatically track and update campaign stats when new campaigns or variants are added
 */
export function useCampaignStatsTracker(options: UseStatsTrackerOptions = {}) {
  const { onStatsUpdate, enableAutoRefresh = true } = options
  const { toast } = useToast()

  const triggerStatsUpdate = useCallback(async (campaignId: string, source: string) => {
    try {
      console.log(`🔄 Triggering stats update for campaign ${campaignId} (source: ${source})`)
      
      // Use dynamic import to avoid circular dependency initialization issues
      const { UnifiedCampaignPerformance } = await import('@/lib/unified-campaign-performance')
      // Fetch performance data to ensure it's cached and up-to-date
      await UnifiedCampaignPerformance.getCampaignPerformance(campaignId)
      await UnifiedCampaignPerformance.getCampaignVariantsPerformance(campaignId)
      
      // Call the optional callback
      onStatsUpdate?.()
      
      toast({
        title: "Stats Updated",
        description: `Performance data refreshed for campaign ${campaignId}`,
      })
    } catch (error) {
      console.error('Error updating campaign stats:', error)
      toast({
        title: "Error",
        description: "Failed to update campaign stats",
        variant: "destructive",
      })
    }
  }, [onStatsUpdate, toast])

  useEffect(() => {
    if (!enableAutoRefresh) return

    // Listen for new campaigns
    const campaignsChannel = supabase
      .channel('campaign_stats_tracker_campaigns')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'campaigns'
        },
        (payload) => {
          console.log('🆕 New campaign detected:', payload.new)
          if (payload.new?.id) {
            triggerStatsUpdate(payload.new.id, 'new_campaign')
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'campaigns'
        },
        (payload) => {
          console.log('📝 Campaign updated:', payload.new)
          if (payload.new?.id && payload.old?.status !== payload.new?.status) {
            triggerStatsUpdate(payload.new.id, 'campaign_status_change')
          }
        }
      )
      .subscribe()

    // Listen for new A/B test variants
    const variantsChannel = supabase
      .channel('campaign_stats_tracker_variants')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ab_test_variants'
        },
        (payload) => {
          console.log('🆕 New variant detected:', payload.new)
          if (payload.new?.smartlead_campaign_id) {
            triggerStatsUpdate(payload.new.smartlead_campaign_id, 'new_variant')
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ab_test_variants'
        },
        (payload) => {
          console.log('📝 Variant updated:', payload.new)
          if (payload.new?.smartlead_campaign_id && payload.old?.status !== payload.new?.status) {
            triggerStatsUpdate(payload.new.smartlead_campaign_id, 'variant_status_change')
          }
        }
      )
      .subscribe()

    // Listen for new performance data
    const performanceChannel = supabase
      .channel('campaign_stats_tracker_performance')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'obsync_dynamic_reports'
        },
        () => {
          console.log('📊 Performance data updated')
          onStatsUpdate?.()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meetings_booked'
        },
        () => {
          console.log('📅 Meeting data updated')
          onStatsUpdate?.()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(campaignsChannel)
      supabase.removeChannel(variantsChannel) 
      supabase.removeChannel(performanceChannel)
    }
  }, [enableAutoRefresh, triggerStatsUpdate, onStatsUpdate])

  return {
    triggerStatsUpdate
  }
}